import React from "react";
import {
  Key,
  Shield,
  CheckCircle,
  Hash,
  Eye,
  EyeOff,
  AlertTriangle,
  RefreshCw,
  Settings,
  Store,
  Printer,
  Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTitanApp } from "@/hooks/useTitanApp";
import { usePrescriptionData } from "@/lib/prescriptionData";
import PrescriptionApprovalModalSkeleton from "./PrescriptionApprovalModalSkeleton";

interface PrescriptionApprovalModalsProps {
  // PIN Authorization Modal props
  showPinAuthModal: boolean;
  onClosePinModal: () => void;
  authStep: number;
  activeCertificate: any;
  selectedPrescriptionsCount: number;
  authPin: string;
  setAuthPin: (pin: string) => void;
  showPin: boolean;
  setShowPin: (show: boolean) => void;
  pinError: string;
  setPinError: (error: string) => void;
  isAuthenticating: boolean;
  onPinAuthorisation: () => void;
  selectedDeliveryOption: "pharmacy" | "print" | "";
  setSelectedDeliveryOption: (option: "pharmacy" | "print" | "") => void;
  selectedPharmacy: string;
  setSelectedPharmacy: (pharmacyId: string) => void;
  pharmacies: any[];
  onProcessPrescriptions: () => void;
  setAuthStep: (step: number) => void;

  // Prescription Image Preview Modal props
  prescriptionImagePreview: string | null;
  setPrescriptionImagePreview: (url: string | null) => void;

  // Prescription Preview Modal props
  previewPrescription: any | null;
  setPreviewPrescription: (prescription: any | null) => void;
  showPrintPreviewLoading: boolean;
}

export function PrescriptionApprovalModals({
  showPinAuthModal,
  onClosePinModal,
  authStep,
  activeCertificate,
  selectedPrescriptionsCount,
  authPin,
  setAuthPin,
  showPin,
  setShowPin,
  pinError,
  setPinError,
  isAuthenticating,
  onPinAuthorisation,
  selectedDeliveryOption,
  setSelectedDeliveryOption,
  selectedPharmacy,
  setSelectedPharmacy,
  pharmacies,
  onProcessPrescriptions,
  setAuthStep,
  prescriptionImagePreview,
  setPrescriptionImagePreview,
  previewPrescription,
  setPreviewPrescription,
  showPrintPreviewLoading,
}: PrescriptionApprovalModalsProps) {
  const { handleUpdatePrescription, pharmaciesOptions } = usePrescriptionData();
  const { patientPrescriptionState } = useTitanApp();
  const {
    isPatientPrescriptionBulkUpdateLoading,
    isPatientPrescriptionByIdLoading,
  } = patientPrescriptionState || {};

  const { appState, onboardingVerificationState } = useTitanApp();
  const { userCertificate } = onboardingVerificationState;

  const { currentUser } = appState || {};
  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = prescriptionImagePreview; // base64 string
    link.download = "prescription.png"; // file name
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return (
    <>
      {/* PIN Authorisation Modal */}
      <Dialog open={showPinAuthModal} onOpenChange={onClosePinModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-600" />
              {authStep === 1
                ? "Authorise Prescription Processing"
                : "Choose Delivery Option"}
            </DialogTitle>
          </DialogHeader>

          {authStep === 1 ? (
            <>
              {/* Step 1: PIN Authorisation */}
              <div className="space-y-6">
                {/* Certificate Information */}
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <Shield className="w-5 h-5 text-green-600" />
                    <div>
                      <h4 className="font-medium text-green-900">
                        Digital Certificate
                      </h4>
                      <p className="text-sm text-green-700">
                        {currentUser?.fullName || ""}
                      </p>
                    </div>
                    <div className="ml-auto">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </span>
                    </div>
                  </div>

                  <div className="text-xs text-green-600 space-y-1">
                    <p>
                      <span className="font-medium">Serial:</span>{" "}
                      {userCertificate?.serialNumber || ""}
                    </p>
                    <p>
                      <span className="font-medium">Expires:</span>{" "}
                      {new Date(
                        userCertificate?.expiryDate || ""
                      ).toLocaleDateString("en-GB")}
                    </p>
                    <p>
                      <span className="font-medium">Issuer:</span>{" "}
                      {userCertificate?.issuer || ""}
                    </p>
                    <p>
                      <span className="font-medium">Purpose:</span> Prescription
                      Authorisation
                    </p>
                  </div>
                </div>

                {/* Processing Summary */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">
                    Processing Summary
                  </h4>
                  <p className="text-sm text-blue-700">
                    You are about to process{" "}
                    <span className="font-semibold">
                      {selectedPrescriptionsCount}
                    </span>{" "}
                    prescription{selectedPrescriptionsCount !== 1 ? "s" : ""}{" "}
                    using your digital certificate.
                  </p>
                  <p className="text-xs text-blue-600 mt-2">
                    This action will digitally sign all selected prescriptions
                    with your certificate and mark them as processed.
                  </p>
                </div>

                {/* PIN Input */}
                <div className="space-y-3">
                  <label
                    htmlFor="auth-pin"
                    className="block text-sm font-medium text-gray-900"
                  >
                    <Hash className="w-4 h-4 inline mr-2" />
                    Enter your 4-digit authorisation PIN:
                  </label>

                  <div className="relative">
                    <input
                      id="auth-pin"
                      type={showPin ? "text" : "password"}
                      value={authPin}
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 4);
                        setAuthPin(value);
                        setPinError("");
                      }}
                      placeholder="Enter 4-digit PIN"
                      maxLength={4}
                      className={`w-full px-3 py-2 border rounded-md text-center text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        pinError ? "border-red-500" : "border-gray-300"
                      }`}
                      disabled={isAuthenticating}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPin(!showPin)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2"
                      disabled={isAuthenticating}
                    >
                      {showPin ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>

                  {pinError && (
                    <p className="text-sm text-red-600 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {pinError}
                    </p>
                  )}
                </div>

                {/* Security Notice */}
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Key className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-amber-800">
                      <p className="font-medium mb-1">Digital Authorisation</p>
                      <p>
                        By entering your PIN, you are authorising the digital
                        signature of multiple prescriptions and taking full
                        clinical responsibility for all selected approvals.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={onClosePinModal}
                  disabled={isAuthenticating}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={onPinAuthorisation}
                  disabled={
                    isAuthenticating || !authPin || authPin.length !== 4
                  }
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {isAuthenticating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Authorizing...
                    </>
                  ) : (
                    <>
                      <Settings className="w-4 h-4 mr-2" />
                      Continue
                    </>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Step 2: Delivery Options */}
              <div className="space-y-6">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">
                    Ready to Process
                  </h4>
                  <p className="text-sm text-blue-700">
                    <span className="font-semibold">
                      {selectedPrescriptionsCount}
                    </span>{" "}
                    prescription{selectedPrescriptionsCount !== 1 ? "s" : ""}{" "}
                    authorised and ready for processing.
                  </p>
                </div>

                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-900">
                    Choose how to process these prescriptions:
                  </label>

                  <div className="space-y-2">
                    <button
                      onClick={() => setSelectedDeliveryOption("pharmacy")}
                      className={`w-full p-4 border rounded-lg text-left transition-all duration-200 ${
                        selectedDeliveryOption === "pharmacy"
                          ? "border-blue-500 ring-2 ring-blue-500/20 bg-blue-50"
                          : "border-gray-300 hover:border-gray-400 bg-white"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Store className="w-5 h-5 text-blue-600" />
                        <div>
                          <div className="font-medium text-gray-900">
                            Send to Pharmacy
                          </div>
                          <div className="text-sm text-gray-500">
                            Send prescriptions directly to a selected pharmacy
                          </div>
                        </div>
                      </div>
                    </button>

                    {/* <button
                      onClick={() => setSelectedDeliveryOption("print")}
                      className={`w-full p-4 border rounded-lg text-left transition-all duration-200 ${
                        selectedDeliveryOption === "print"
                          ? "border-blue-500 ring-2 ring-blue-500/20 bg-blue-50"
                          : "border-gray-300 hover:border-gray-400 bg-white"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Printer className="w-5 h-5 text-blue-600" />
                        <div>
                          <div className="font-medium text-gray-900">
                            Print Prescriptions
                          </div>
                          <div className="text-sm text-gray-500">
                            Generate prescriptions for local printing
                          </div>
                        </div>
                      </div>
                    </button> */}
                  </div>

                  {/* Pharmacy Selection */}
                  {selectedDeliveryOption === "pharmacy" && (
                    <div className="mt-4 space-y-2">
                      <label className="block text-sm font-medium text-gray-900">
                        Select Pharmacy:
                      </label>
                      <select
                        value={selectedPharmacy}
                        onChange={(e) => setSelectedPharmacy(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Choose a pharmacy...</option>
                        {pharmaciesOptions.map((pharmacy) => (
                          <option key={pharmacy} value={pharmacy}>
                            {pharmacy}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setAuthStep(1)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={onProcessPrescriptions}
                  disabled={
                    !selectedDeliveryOption ||
                    (selectedDeliveryOption === "pharmacy" && !selectedPharmacy)
                  }
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  {isPatientPrescriptionBulkUpdateLoading
                    ? "Processing..."
                    : "Process Prescriptions"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Prescription Image Preview Modal */}
      <Dialog
        open={!!prescriptionImagePreview}
        onOpenChange={() => setPrescriptionImagePreview(null)}
      >
        <DialogContent className="max-w-4xl max-h-[170vh] p-0">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              Prescription Image Preview
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6">
            {showPrintPreviewLoading ? (
              <PrescriptionApprovalModalSkeleton />
            ) : (
              <div className="relative overflow-y-auto max-h-[75vh] custom-scrollbar">
                <img
                  src={prescriptionImagePreview}
                  alt="Prescription"
                  className="w-full h-auto max-h-[170vh] object-contain rounded-lg border border-gray-200"
                />
                <div className="mt-4 flex justify-end gap-2">
                  <Button variant="outline" onClick={handleDownload}>
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => setPrescriptionImagePreview(null)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Prescription Preview Dialog */}
      {previewPrescription && (
        <Dialog
          open={!!previewPrescription}
          onOpenChange={() => setPreviewPrescription(null)}
        >
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Prescription Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">Patient Information</h3>
                <p>
                  <strong>Name:</strong> {previewPrescription.patientName}
                </p>
                <p>
                  <strong>NHS Number:</strong> {previewPrescription.patientNHS}
                </p>
                <p>
                  <strong>Allergies:</strong>{" "}
                  {previewPrescription.allergies?.join(", ") || "None"}
                </p>
              </div>
              <div>
                <h3 className="font-medium mb-2">Prescription Details</h3>
                {previewPrescription.medications.map((med: any) => (
                  <div key={med.id} className="border rounded p-3 mb-2">
                    <p>
                      <strong>Medication:</strong> {med.medication}
                    </p>
                    <p>
                      <strong>Dosage:</strong> {med.dosage}
                    </p>
                    <p>
                      <strong>Quantity:</strong> {med.quantity}
                    </p>
                    <p>
                      <strong>Duration:</strong> {med.duration}
                    </p>
                    <p>
                      <strong>Indication:</strong> {med.indication}
                    </p>
                  </div>
                ))}
                <p>
                  <strong>Prescribed by:</strong>{" "}
                  {previewPrescription.prescribedBy}
                </p>
                <p>
                  <strong>Notes:</strong> {previewPrescription.notes || "None"}
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
